import { useState, useEffect, lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Zap, Target, Gift, Lock, Loader2, RotateCcw, Send } from 'lucide-react';
import { VSLPlayer } from './VSLPlayer';
const VSLDialog = lazy(() => import('./VSLPlayer').then((m) => ({ default: m.VSLDialog })));
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';

const PLACEHOLDERS = [
  'Ex: Quero um post para Instagram sobre café especial...',
  'Ex: Preciso de um código Python para web scraping...',
  'Ex: Crie uma imagem futurista de uma cidade neon...',
  'Ex: Escreva um e-mail de vendas persuasivo para SaaS...',
  'Ex: Gere uma descrição de produto para e-commerce...',
];

const descriptionSchema = z.string().trim().min(10, 'Descreva sua ideia com pelo menos 10 caracteres').max(500, 'Máximo de 500 caracteres');

interface PreviewResult {
  recommendedModel: { name: string; code: string };
  prompt: string;
}

export function Hero() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vslOpen, setVslOpen] = useState(false);
  const [idea, setIdea] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);

  useEffect(() => {
    if (idea || result) return;
    const id = setInterval(() => setPlaceholderIdx((i) => (i + 1) % PLACEHOLDERS.length), 3000);
    return () => clearInterval(id);
  }, [idea, result]);

  const handleGenerate = async () => {
    const parsed = descriptionSchema.safeParse(idea);
    if (!parsed.success) {
      toast({ title: 'Ops!', description: parsed.error.issues[0].message, variant: 'destructive' });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('public-generate-preview', {
        body: { description: parsed.data },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.prompt || !data?.recommendedModel) throw new Error('Resposta inválida');
      setResult(data as PreviewResult);
    } catch (err: any) {
      toast({
        title: 'Erro ao gerar prompt',
        description: err?.message || 'Tente novamente em instantes.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setIdea('');
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 pb-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-hero-pattern" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[48px]" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/20 rounded-full blur-[40px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full liquid-glass-subtle border-primary/30 mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Prompts otimizados por IA</span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up">
            Se torne um profissional de{' '}
            <span className="metallic-text">IA em 2 minutos</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-foreground/85 mb-8 max-w-2xl mx-auto animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.1s' }}>
            Digite sua ideia abaixo e veja a mágica acontecer. Descubra qual a melhor IA para a tarefa e receba um prompt otimizado em segundos.
          </p>

          {/* Interactive prompt area */}
          <div className="max-w-3xl mx-auto mb-8 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            {!result && !loading && (
              <div className="relative group animate-glow-pulse rounded-2xl">
                {/* Animated gradient border */}
                <div className="absolute -inset-[3px] rounded-2xl bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_200%] animate-gradient opacity-100" />
                <div className="relative liquid-glass-strong liquid-shine rounded-2xl p-2">
                <Textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value.slice(0, 500))}
                  placeholder={PLACEHOLDERS[placeholderIdx]}
                  className="min-h-[120px] md:min-h-[140px] resize-none border-0 bg-transparent text-base md:text-lg text-foreground focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground relative z-10"
                  maxLength={500}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                />
                <div className="flex items-center justify-between gap-3 px-3 pb-2 pt-1 relative z-10">
                  <span className="text-xs text-muted-foreground">{idea.length}/500</span>
                  <Button
                    onClick={handleGenerate}
                    variant="hero"
                    size="lg"
                    disabled={idea.trim().length < 10}
                    className="group">
                    <Send className="w-4 h-4" />
                    Gerar Prompt Grátis
                  </Button>
                </div>
                </div>
              </div>
            )}

            {loading && (
              <Card className="liquid-glass-strong border-primary/30 p-6 text-left animate-fade-in">
                <div className="flex items-center gap-2 mb-4 relative z-10">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  <span className="text-sm font-medium">Gerando seu prompt otimizado...</span>
                </div>
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-2" />
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-4 w-4/5" />
              </Card>
            )}

            {result && !loading && (() => {
              // Show first ~6 real lines, then render redacted skeleton bars for the rest.
              // The actual hidden prompt text never reaches the DOM — impossible to extract via DevTools.
              const lines = result.prompt.split('\n');
              const sharpText = lines.slice(0, 3).join('\n');
              const fadeLine4 = lines[3] ?? '';
              const fadeLine5 = lines[4] ?? '';
              const fadeLine6 = lines[5] ?? '';
              const hiddenCount = Math.max(lines.length - 6, 8);

              // Deterministic pseudo-random widths so the skeleton looks like real prose
              const widths = Array.from({ length: hiddenCount }, (_, i) => {
                const seed = (i * 9301 + 49297) % 233280;
                const r = seed / 233280;
                return 45 + Math.floor(r * 50); // 45% – 95%
              });

              return (
              <Card className="liquid-glass-strong liquid-shine border-primary/30 p-6 text-left animate-fade-in-up">
                {/* Recommended AI */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border/50">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">IA recomendada</p>
                    <p className="font-semibold text-base">{result.recommendedModel.name}</p>
                  </div>
                </div>

                {/* Prompt preview with scrollable redacted section */}
                <div className="relative">
                  <div
                    className="max-h-[280px] overflow-y-auto pr-2 select-none scroll-smooth [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary/30 [&::-webkit-scrollbar-thumb]:rounded-full"
                    onCopy={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <pre className="whitespace-pre-wrap break-words font-mono text-sm text-foreground/90">
                      {sharpText}
                    </pre>
                    {fadeLine4 && (
                      <pre className="whitespace-pre-wrap break-words font-mono text-sm text-foreground/80 blur-[1.5px] mt-1 select-none">
                        {fadeLine4}
                      </pre>
                    )}
                    {fadeLine5 && (
                      <pre className="whitespace-pre-wrap break-words font-mono text-sm text-foreground/70 blur-[3px] mt-1 select-none">
                        {fadeLine5}
                      </pre>
                    )}
                    {fadeLine6 && (
                      <pre className="whitespace-pre-wrap break-words font-mono text-sm text-foreground/60 blur-[5px] mt-1 select-none">
                        {fadeLine6}
                      </pre>
                    )}

                    {/* Redacted skeleton — no real text in the DOM */}
                    <div aria-hidden="true" className="mt-3 space-y-2 pointer-events-none pb-16">
                      {widths.map((w, i) => (
                        <div
                          key={i}
                          className="h-3 rounded-md bg-gradient-to-r from-foreground/20 via-foreground/30 to-foreground/15"
                          style={{ width: `${w}%` }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Sticky lock overlay at bottom of scroll container */}
                  <div className="absolute inset-x-0 bottom-0 h-[90px] bg-gradient-to-b from-transparent via-background/90 to-background flex flex-col items-center justify-end pb-1 pointer-events-none">
                    <div className="flex items-center gap-2 mb-2 px-4 py-2 rounded-full bg-primary/15 border border-primary/40 backdrop-blur-sm">
                      <Lock className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">
                        Cadastre-se grátis para ver o prompt completo
                      </span>
                    </div>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    variant="hero"
                    size="lg"
                    onClick={() => navigate('/auth?mode=register')}
                    className="flex-1 group">
                    <Gift className="w-4 h-4" />
                    Criar Conta Grátis e Ganhar 5 Créditos
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button
                    variant="glass"
                    size="lg"
                    onClick={handleReset}>
                    <RotateCcw className="w-4 h-4" />
                    Outra ideia
                  </Button>
                </div>
              </Card>
              );
            })()}
          </div>

          {/* Free credits badge */}
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl liquid-glass-subtle liquid-shine border-primary/30 mb-8 animate-fade-in-up animate-gentle-bounce relative overflow-hidden" style={{ animationDelay: '0.2s' }}>
            <div className="absolute inset-0 animate-shimmer pointer-events-none" />
            <Gift className="w-5 h-5 text-primary animate-pulse relative z-10" />
            <span className="text-sm font-medium relative z-10">
              <span className="text-primary font-bold text-lg">5</span> Créditos Grátis ao criar sua conta
            </span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-lg mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-primary mb-1">
                <Zap className="w-5 h-5" />
              </div>
              <p className="text-2xl md:text-3xl font-extrabold text-foreground">10x</p>
              <p className="text-xs text-foreground/70 font-medium">Mais produtivo</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-primary mb-1">
                <Target className="w-5 h-5" />
              </div>
              <p className="text-2xl md:text-3xl font-extrabold text-foreground">95%</p>
              <p className="text-xs text-foreground/70 font-medium">de satisfação</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-primary mb-1">
                <Sparkles className="w-5 h-5" />
              </div>
              <p className="text-2xl md:text-3xl font-extrabold text-foreground">15+</p>
              <p className="text-xs text-foreground/70 font-medium">IAs suportadas</p>
            </div>
          </div>
        </div>

        {/* VSL intro text */}
        <div className="mt-16 max-w-2xl mx-auto text-center animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
          <p className="text-lg md:text-xl font-bold mb-2 text-foreground">
            Veja em <span className="text-primary">menos de 2 minutos</span> como funciona
          </p>
          <p className="text-base text-foreground/75">
            Descubra como transformar qualquer ideia em um prompt perfeito no vídeo abaixo.
          </p>
        </div>

        {/* VSL */}
        <div className="mt-6 max-w-5xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <VSLPlayer />
        </div>

        {/* Secondary "como funciona" CTA */}
        <div className="flex justify-center mt-6 animate-fade-in-up" style={{ animationDelay: '0.45s' }}>
          <Button variant="glass" size="lg" onClick={() => setVslOpen(true)}>
            Ver como funciona
          </Button>
        </div>
      </div>

      {vslOpen && (
        <Suspense fallback={null}>
          <VSLDialog open={vslOpen} onOpenChange={setVslOpen} />
        </Suspense>
      )}
    </section>
  );
}
